import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';

// Self-host EB Garamond + Manrope (Fontsource).
// Phase 27.34 — font weights pruning : drop 500 + 600 (jamais utilisés sur
// font-display dans le codebase — vérifié grep cross-files). font-bold sur
// stats-strip = synthesized 700 par browser (acceptable pour stat numbers
// 7-10rem). Manrope variable file contient déjà toutes les weights 300-800.
import '@fontsource/eb-garamond/400.css';
import '@fontsource/eb-garamond/400-italic.css';
import '@fontsource-variable/manrope/index.css';

// Design system Luminous Editorial (Tailwind v4 + OKLCH tokens)
import './styles.css';

import { routeTree } from './routeTree.gen';
import { LanguageProvider } from '@/lib/LanguageContext';
import { CalendlyProvider } from '@/lib/CalendlyContext';
import { CarnetProvider } from '@/lib/CarnetContext';
import { LexiqueProvider } from '@/lib/LexiqueContext';
import { ColophonProvider } from '@/lib/ColophonContext';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { CookieBanner } from '@/components/ui/cookie-banner';
import { initWebVitals } from '@/lib/perf/web-vitals';
import { AnalyticsGate } from '@/lib/analytics/consent-gate';
import { initSentry, reportErrorBoundaryError } from '@/lib/sentry-client';

// Phase 27.36 — init Sentry AVANT createRoot pour capter les erreurs early.
// No-op silencieux si VITE_SENTRY_DSN absent ou === 'REPLACE_AT_END'.
initSentry();

// Phase 9 perf — preload route chunks on hover/focus intent (smoother nav).
// Phase 27.4 — View Transitions API : transitions fluides cross-routes via
// document.startViewTransition (auto fallback no-op si browser unsupported).
// Keyframes custom dans styles.css (::view-transition-old/new(root)).
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultViewTransition: true
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <ErrorBoundary onError={reportErrorBoundaryError}>
        <LanguageProvider>
          <CalendlyProvider>
            <CarnetProvider>
              <LexiqueProvider>
                <ColophonProvider>
                  <TooltipProvider>
                    <RouterProvider router={router} />
                    <Toaster />
                    <CookieBanner />
                    <AnalyticsGate />
                  </TooltipProvider>
                </ColophonProvider>
              </LexiqueProvider>
            </CarnetProvider>
          </CalendlyProvider>
        </LanguageProvider>
      </ErrorBoundary>
    </StrictMode>
  );

  // Phase 9 perf — Core Web Vitals reporting (LCP/INP/CLS/FCP/TTFB).
  // Default reporter = console.warn for dev visibility. Phase 10 will pipe to GA4.
  initWebVitals();
}
